import React, { useState } from "react";
import { toast } from "react-hot-toast";

// Components
import RulesAndFAQs from "./RulesAndFAQs";
import Clarifications from "./Clarifications";

// Hooks
import useAPI from "../../hooks/useAPI";

// Utils
import { isValidEmail, isAllDigits, isValidName } from "../../utils/validation";
import { getFormattedTimestamp } from "../../utils/formatters";

// Matrial UI Compenets
import {
  Box,
  Zoom,
  Select,
  Tooltip,
  MenuItem,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  InputAdornment,
  CircularProgress,
} from "@mui/material";

// Matrial UI Icons
import PersonIcon from "@mui/icons-material/Person";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";

const preferences = [
  { category: "HTML CSS JS" },
  { category: "Python GUI" },
  { category: "React JS" },
  { category: "Node Js" },
  { category: "Flutter" },
  { category: "Django" },
  { category: "Spring Boot" },
  { category: "NLP" },
  { category: "Systems Programming" },
];

const juniorCategory = ["HTML CSS JS", "Python GUI"];

const WinterOfCodeRegister = () => {
  const { POST } = useAPI();

  const [studentId, setStudentId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [preference1, setPreference1] = useState("");
  const [preference2, setPreference2] = useState("");
  const [preference3, setPreference3] = useState("");
  const [feedback, setFeedback] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isJustVerify, setIsJustVerify] = useState(false);

  const isFromValid = () => {
    return (
      studentId.length === 9 &&
      studentId.startsWith("20") &&
      name.trim() !== "" &&
      isValidEmail(email) &&
      phoneNumber.length === 11 &&
      preference1 !== ""
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsJustVerify((prev) => true);

    if (!isFromValid()) return;

    if (
      !studentId.startsWith("20240") &&
      (juniorCategory.includes(preference1) ||
        juniorCategory.includes(preference2) ||
        juniorCategory.includes(preference3))
    ) {
      toast(`"HTML CSS JS" and "Python GUI" categories only for 1st years`, {
        icon: <CancelRoundedIcon />,
        style: {
          borderRadius: "10px",
          background: "white",
          color: "black",
        },
      });
      return;
    }

    setIsLoading((prev) => true);

    const data = {
      studentId,
      name: name.trim(),
      email,
      phoneNumber,
      preference1,
      preference2,
      preference3,
      feedback: feedback.trim(),
    };

    const sheetsData = {
      spreadsheetId: "1SYECE6_0pjnsQb9bFFQ7UaxEdWKhPGVmNgyuBIotU_8",
      sheetName: "Sheet1",
      data: [
        studentId,
        name.trim(),
        email,
        phoneNumber,
        preference1,
        preference2,
        preference3,
        feedback.trim(),
        getFormattedTimestamp(),
      ],
    };

    try {
      await POST("/data-services/store-data", sheetsData);
      await POST("/mail-services/send-mail/register", data);
      toast("Submitted Successfully", {
        icon: <CheckCircleRoundedIcon />,
        style: {
          borderRadius: "10px",
          background: "white",
          color: "black",
        },
      });

      setIsJustVerify((prev) => false);
      setStudentId("");
      setName("");
      setEmail("");
      setPhoneNumber("");
      setPreference1("");
      setPreference2("");
      setPreference3("");
      setFeedback("");
    } catch (error) {
      console.log(error);
      toast(error.response?.data?.message || "Submission Unsuccessful", {
        icon: <CancelRoundedIcon />,
        style: {
          borderRadius: "10px",
          background: "white",
          color: "black",
        },
      });
    } finally {
      setIsLoading((prev) => false);
    }
  };

  const handleStudentIdChange = (e) => {
    const value = e.target.value;
    // Allow only digits
    if (isAllDigits(value) && value.length < 10) {
      //allow only digits
      setStudentId(value);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    // Allow only letters and spaces
    if (isValidName(value) && value.length < 256) {
      //allow only digits a-zA-Z[space]
      setName(value);
    }
  };

  const handleEmailChange = (e) => {
    if (e.target.value.length < 256) {
      setEmail(e.target.value);
    }
  };

  const handlePhoneNumberChange = (e) => {
    if (e.target.value.length <= 3) return;

    let value = e.target.value.replace("+91 ", "").trim();
    value = value.replace(" ", "").trim();

    // Allow only digits
    if (isAllDigits(value) && value.length <= 10 && value[0] !== "0") {
      //allow only digits
      value =
        value.substring(0, 5) +
        (value.length > 5 ? " " + value.substring(5) : "");
      setPhoneNumber(value);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          color: "white",
          mb: 6,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            mt: 4,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem", lg: "3rem" }, // Adjust font size
            textAlign: { xs: "center", sm: "left" }, // Center text on small screens, left-align on larger screens
          }}
        >
          Register to Winter of Code
        </Typography>
        <Box
          sx={{
            width: "100%",
            maxWidth: 500,
            p: 2,
            my: 4,
            borderRadius: 4,
            backdropFilter: "blur(10px)",
            zIndex: 3,
            bgcolor: "rgba(255, 255, 255, 0.04)", // Set default text color to white
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <TextField
              value={studentId}
              onChange={handleStudentIdChange}
              name="studentId"
              id="student-id"
              label="Student ID"
              placeholder="e.g. 202201503"
              variant="outlined"
              fullWidth
              required
              size="small"
              autoComplete="on"
              error={
                isJustVerify &&
                (studentId.length !== 9 || !studentId.startsWith("20"))
              }
              helperText={
                isJustVerify &&
                (studentId === ""
                  ? "Student Id cannot be empty"
                  : studentId.length !== 9 || !studentId.startsWith("20")
                  ? "Please, Enter valid student id"
                  : "")
              }
              FormHelperTextProps={{
                sx: {
                  color: "red", // Change the helper text color
                  fontWeight: "bold",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "white" }} />
                  </InputAdornment>
                ),
                endAdornment:
                  isJustVerify &&
                  (studentId.length !== 9 || !studentId.startsWith("20")) ? (
                    <InputAdornment position="end">
                      <Tooltip
                        TransitionComponent={Zoom}
                        title={
                          studentId === ""
                            ? "Student Id cannot be empty"
                            : studentId.length !== 9 ||
                              !studentId.startsWith("20")
                            ? "Please, Enter valid student id"
                            : ""
                        }
                        placement="bottom"
                        arrow
                        leaveDelay={0}
                        enterDelay={0}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              border: "1px solid black",
                              bgcolor: "white",
                              color: "black",
                              transition: "none",
                              fontWeight: "bold",
                            },
                          },
                          arrow: {
                            sx: {
                              color: "black",
                            },
                          },
                        }}
                      >
                        <WarningAmberRoundedIcon sx={{ color: "white" }} />
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              inputProps={{
                required: false, // disables the default pop-up message
              }}
              sx={{
                color: "white", // Set default text color to white
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  fontWeight: "bold",
                  color: "white", // Text color inside the input is white
                  borderColor: "white", // Set default border color to white
                  "& .MuiInputBase-input": {
                    color: "white", // Set text color inside the input field to white
                    borderColor: "white",
                    fontWeight: "bold",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white", // Keep the border color white when focused
                    color: "white",
                    fontWeight: "bold",
                  },
                  "& fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Default border color for the fieldset
                  },
                  "&:hover fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Border color when hovered
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white", // Change label color to white
                  fontWeight: "bold",
                  "&.Mui-focused": {
                    color: "white", // Keep label color white when focused
                    fontWeight: "bold",
                  },
                },
                "& .MuiInputAdornment-root": {
                  color: "white", // Set adornment color to white
                  fontWeight: "bold",
                },
                "& .MuiSvgIcon-root": {
                  color: "white", // Set icons color to white
                  fontWeight: "bold",
                },
              }}
            />
            <TextField
              value={name}
              onChange={handleNameChange}
              name="name"
              id="name"
              label="Name"
              placeholder="e.g. manoj"
              variant="outlined"
              fullWidth
              required
              size="small"
              autoComplete="on"
              error={isJustVerify && name.trim() === ""}
              helperText={
                isJustVerify &&
                (name.trim() === "" ? "Name cannot be empty" : "")
              }
              FormHelperTextProps={{
                sx: {
                  color: "red", // Change the helper text color
                  fontWeight: "bold",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "#333333" }} />
                  </InputAdornment>
                ),
                endAdornment:
                  isJustVerify && name.trim() === "" ? (
                    <InputAdornment position="end">
                      <Tooltip
                        TransitionComponent={Zoom}
                        title={name.trim() === "" ? "Name cannot be empty" : ""}
                        placement="bottom"
                        arrow
                        leaveDelay={0}
                        enterDelay={0}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              border: "1px solid black",
                              bgcolor: "white",
                              color: "black",
                              transition: "none",
                              fontWeight: "bold",
                            },
                          },
                          arrow: {
                            sx: {
                              color: "black",
                            },
                          },
                        }}
                      >
                        <WarningAmberRoundedIcon sx={{ color: "#333333" }} />
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              inputProps={{
                required: false, // disables the default pop-up message
              }}
              sx={{
                color: "white", // Set default text color to white
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  fontWeight: "bold",
                  color: "white", // Text color inside the input is white
                  borderColor: "white", // Set default border color to white
                  "& .MuiInputBase-input": {
                    color: "white", // Set text color inside the input field to white
                    borderColor: "white",
                    fontWeight: "bold",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white", // Keep the border color white when focused
                    color: "white",
                    fontWeight: "bold",
                  },
                  "& fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Default border color for the fieldset
                  },
                  "&:hover fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Border color when hovered
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white", // Change label color to white
                  fontWeight: "bold",
                  "&.Mui-focused": {
                    color: "white", // Keep label color white when focused
                    fontWeight: "bold",
                  },
                },
                "& .MuiInputAdornment-root": {
                  color: "white", // Set adornment color to white
                  fontWeight: "bold",
                },
                "& .MuiSvgIcon-root": {
                  color: "white", // Set icons color to white
                  fontWeight: "bold",
                },
              }}
            />
            <TextField
              value={email}
              onChange={handleEmailChange}
              name="email"
              id="email"
              label="Email Id"
              placeholder="e.g. manoj@gmail.com"
              variant="outlined"
              fullWidth
              required
              size="small"
              autoComplete="on"
              error={isJustVerify && !isValidEmail(email)}
              helperText={
                isJustVerify &&
                (email.trim() === ""
                  ? "Email id cannot be empty"
                  : !isValidEmail(email)
                  ? "Email address is not valid"
                  : "")
              }
              FormHelperTextProps={{
                sx: {
                  color: "red", // Change the helper text color
                  fontWeight: "bold",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailRoundedIcon sx={{ color: "#333333" }} />
                  </InputAdornment>
                ),
                endAdornment:
                  isJustVerify && !isValidEmail(email) ? (
                    <InputAdornment position="end">
                      <Tooltip
                        TransitionComponent={Zoom}
                        title={
                          email.trim() === ""
                            ? "Email id cannot be empty"
                            : "Email address is not valid"
                        }
                        placement="bottom"
                        arrow
                        leaveDelay={0}
                        enterDelay={0}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              border: "1px solid black",
                              bgcolor: "white",
                              color: "black",
                              transition: "none",
                              fontWeight: "bold",
                            },
                          },
                          arrow: {
                            sx: {
                              color: "black",
                            },
                          },
                        }}
                      >
                        <WarningAmberRoundedIcon sx={{ color: "#333333" }} />
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              inputProps={{
                required: false, // disables the default pop-up message
              }}
              sx={{
                color: "white", // Set default text color to white
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  fontWeight: "bold",
                  color: "white", // Text color inside the input is white
                  borderColor: "white", // Set default border color to white
                  "& .MuiInputBase-input": {
                    color: "white", // Set text color inside the input field to white
                    borderColor: "white",
                    fontWeight: "bold",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white", // Keep the border color white when focused
                    color: "white",
                    fontWeight: "bold",
                  },
                  "& fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Default border color for the fieldset
                  },
                  "&:hover fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Border color when hovered
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white", // Change label color to white
                  fontWeight: "bold",
                  "&.Mui-focused": {
                    color: "white", // Keep label color white when focused
                    fontWeight: "bold",
                  },
                },
                "& .MuiInputAdornment-root": {
                  color: "white", // Set adornment color to white
                  fontWeight: "bold",
                },
                "& .MuiSvgIcon-root": {
                  color: "white", // Set icons color to white
                  fontWeight: "bold",
                },
              }}
            />
            <TextField
              value={`+91 ${phoneNumber}`}
              onChange={handlePhoneNumberChange}
              name="phoneNumber"
              id="phone-number"
              label="PhoneNumber"
              placeholder="e.g. +91 99099 09909"
              variant="outlined"
              fullWidth
              required
              size="small"
              autoComplete="on"
              error={isJustVerify && phoneNumber.length !== 11}
              helperText={
                isJustVerify &&
                (phoneNumber === ""
                  ? "Phone number cannot be empty"
                  : phoneNumber.length !== 11
                  ? "Please, Enter valid phone number"
                  : "")
              }
              FormHelperTextProps={{
                sx: {
                  color: "red", // Change the helper text color
                  fontWeight: "bold",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CallRoundedIcon sx={{ color: "#333333" }} />
                  </InputAdornment>
                ),
                endAdornment:
                  isJustVerify && phoneNumber.length !== 11 ? (
                    <InputAdornment position="end">
                      <Tooltip
                        TransitionComponent={Zoom}
                        title={
                          phoneNumber === ""
                            ? "Phone number cannot be empty"
                            : phoneNumber.length < 11
                            ? "Please, Enter valid phone number"
                            : ""
                        }
                        placement="bottom"
                        arrow
                        leaveDelay={0}
                        enterDelay={0}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              border: "1px solid black",
                              bgcolor: "white",
                              color: "black",
                              transition: "none",
                              fontWeight: "bold",
                            },
                          },
                          arrow: {
                            sx: {
                              color: "black",
                            },
                          },
                        }}
                      >
                        <WarningAmberRoundedIcon sx={{ color: "#333333" }} />
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              inputProps={{
                required: false, // disables the default pop-up message
              }}
              sx={{
                color: "white", // Set default text color to white
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  fontWeight: "bold",
                  color: "white", // Text color inside the input is white
                  borderColor: "white", // Set default border color to white
                  "& .MuiInputBase-input": {
                    color: "white", // Set text color inside the input field to white
                    borderColor: "white",
                    fontWeight: "bold",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white", // Keep the border color white when focused
                    color: "white",
                    fontWeight: "bold",
                  },
                  "& fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Default border color for the fieldset
                  },
                  "&:hover fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Border color when hovered
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white", // Change label color to white
                  fontWeight: "bold",
                  "&.Mui-focused": {
                    color: "white", // Keep label color white when focused
                    fontWeight: "bold",
                  },
                },
                "& .MuiInputAdornment-root": {
                  color: "white", // Set adornment color to white
                  fontWeight: "bold",
                },
                "& .MuiSvgIcon-root": {
                  color: "white", // Set icons color to white
                  fontWeight: "bold",
                },
              }}
            />
            <FormControl size="small" fullWidth required>
              <InputLabel
                id="preference1-label"
                sx={{
                  color: "white",
                  "&.Mui-focused": {
                    color: "white", // Set label color to white when focused
                  },
                }}
              >
                Preference1
              </InputLabel>
              <Select
                labelId="preference1-label"
                name="preference1"
                id="preference1"
                label="Preference1"
                value={preference1}
                onChange={(e) => {
                  if (e.target.value === preference2) setPreference2("");
                  else if (e.target.value === preference3) setPreference3("");
                  setPreference1(e.target.value);
                }}
                error={isJustVerify && preference1 === ""}
                sx={{
                  borderRadius: 3,
                  color: "white", // Set text color to white
                  ".MuiSelect-icon": {
                    color: "white", // Icon color
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Border color when not focused
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Hover border color to white
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Focused border color to white
                  },
                  "&.Mui-focused .MuiInputLabel-root": {
                    color: "white", // Focused label color to white
                  },
                }}
              >
                {preferences.map((preference, index) => {
                  // Default case when studentId starts with 20240
                  return (
                    <MenuItem key={index} value={preference.category}>
                      <Typography fontWeight="bold">
                        {preference.category}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel
                id="preference2-label"
                sx={{
                  color: "white",
                  "&.Mui-focused": {
                    color: "white", // Set label color to white when focused
                  },
                }}
              >
                Preference2
              </InputLabel>
              <Select
                labelId="preference2-label"
                name="preference2"
                id="preference2"
                label="Preference2"
                value={preference2}
                onChange={(e) => {
                  if (e.target.value === preference3) setPreference3("");
                  setPreference2(e.target.value);
                }}
                sx={{
                  borderRadius: 3,
                  color: "white", // Set text color to white
                  ".MuiSelect-icon": {
                    color: "white", // Icon color
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Border color when not focused
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Hover border color to white
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Focused border color to white
                  },
                  "&.Mui-focused .MuiInputLabel-root": {
                    color: "white", // Focused label color to white
                  },
                }}
              >
                {preferences.map((preference, index) => {
                  if (preference.category === preference1) return;

                  // Default case when studentId starts with "20240" or the condition above is not met
                  return (
                    <MenuItem key={index} value={preference.category}>
                      <Typography fontWeight="bold">
                        {preference.category}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel
                id="preference3-label"
                sx={{
                  color: "white",
                  "&.Mui-focused": {
                    color: "white", // Set label color to white when focused
                  },
                }}
              >
                Preference3
              </InputLabel>
              <Select
                labelId="preference3-label"
                name="preference3"
                id="preference3"
                label="Preference3"
                value={preference3}
                onChange={(e) => setPreference3(e.target.value)}
                sx={{
                  borderRadius: 3,
                  color: "white", // Set text color to white
                  ".MuiSelect-icon": {
                    color: "white", // Icon color
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Border color when not focused
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Hover border color to white
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white", // Focused border color to white
                  },
                  "&.Mui-focused .MuiInputLabel-root": {
                    color: "white", // Focused label color to white
                  },
                }}
              >
                {preferences.map((preference, index) => {
                  if (
                    preference.category === preference1 ||
                    preference.category === preference2
                  )
                    return;

                  // Default case when studentId starts with "20240" or the condition above is not met
                  return (
                    <MenuItem key={index} value={preference.category}>
                      <Typography fontWeight="bold">
                        {preference.category}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              name="feedback"
              id="feedback"
              label="Feedback"
              placeholder=""
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              size="small"
              autoComplete="on"
              inputProps={{
                required: false, // disables the default pop-up message
              }}
              sx={{
                color: "white", // Set default text color to white
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  fontWeight: "bold",
                  color: "white", // Text color inside the input is white
                  borderColor: "white", // Set default border color to white
                  "& .MuiInputBase-input": {
                    color: "white", // Set text color inside the input field to white
                    borderColor: "white",
                    fontWeight: "bold",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white", // Keep the border color white when focused
                    color: "white",
                    fontWeight: "bold",
                  },
                  "& fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Default border color for the fieldset
                  },
                  "&:hover fieldset": {
                    fontWeight: "bold",
                    borderColor: "white", // Border color when hovered
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white", // Change label color to white
                  fontWeight: "bold",
                  "&.Mui-focused": {
                    color: "white", // Keep label color white when focused
                    fontWeight: "bold",
                  },
                },
                "& .MuiInputAdornment-root": {
                  color: "white", // Set adornment color to white
                  fontWeight: "bold",
                },
                "& .MuiSvgIcon-root": {
                  color: "white", // Set icons color to white
                  fontWeight: "bold",
                },
              }}
            />

            <button
              type="submit"
              name="submit"
              style={{
                backgroundColor: "white",
                borderColor: "white",
              }}
            >
              <Typography fontWeight="bold" color="#023047">
                {isLoading ? "Submitting" : "Submit"}
              </Typography>
              {isLoading ? (
                <CircularProgress
                  size={20}
                  thickness={7}
                  sx={{
                    mx: 2,
                    color: "#023047",
                    "& circle": { strokeLinecap: "round" },
                  }}
                />
              ) : null}
            </button>
          </form>
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <RulesAndFAQs />
      </Box>
      <Box sx={{ mb: 4 }}>
        <Clarifications />
      </Box>
    </>
  );
};

export default WinterOfCodeRegister;
